import { motion } from "framer-motion";
import { TrendingDown } from "lucide-react";
import SceneShell from "../components/SceneShell";
import CountUp from "../components/CountUp";
import MoneyFlow from "../components/MoneyFlow";

/**
 * Scene 28 — Impact moment · £242,500 saved at 50k volume.
 * Mirrors Scene 26 but bigger.
 */

export default function Scene28_Saving240k() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-saving-240k">
      <MoneyFlow density={48} color="rgba(150,193,31,0.85)" />
      <MoneyFlow density={28} color="rgba(0,222,182,0.7)" />
      <MoneyFlow density={14} color="rgba(233,168,32,0.7)" symbol="£" />

      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at center, rgba(150,193,31,0.24) 0%, transparent 55%)",
            "radial-gradient(ellipse at center, rgba(150,193,31,0.40) 0%, transparent 55%)",
            "radial-gradient(ellipse at center, rgba(150,193,31,0.24) 0%, transparent 55%)",
          ],
        }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Concentric rings of energy */}
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="absolute top-1/2 left-1/2 rounded-full border pointer-events-none"
          style={{
            width: 200,
            height: 200,
            borderColor: "rgba(150,193,31,0.5)",
            translate: "-50% -50%",
          }}
          animate={{ scale: [1, 5], opacity: [0.55, 0] }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeOut",
            delay: 1.4 + i * 1.2,
          }}
        />
      ))}

      <div className="relative max-w-[1600px] mx-auto w-full text-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mx-auto flex items-center justify-center gap-2"
        >
          <TrendingDown className="w-3.5 h-3.5" />
          Impact at scale
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.6, filter: "blur(32px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.7, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8"
          data-testid="saving-240k-number"
        >
          <div
            className="kn-display tnum leading-[0.85]"
            style={{
              fontSize: "clamp(96px, 16vw, 240px)",
              background:
                "linear-gradient(135deg, #96C11F 0%, #00deb6 50%, #96C11F 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              textShadow: "0 0 120px rgba(150,193,31,0.55)",
              whiteSpace: "nowrap",
            }}
          >
            <CountUp
              from={0}
              to={242500}
              prefix="£"
              duration={2.8}
              startDelay={1.4}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 2.6 }}
          className="mt-8 kn-display text-3xl sm:text-5xl lg:text-6xl text-white"
        >
          saved <span className="text-white/55">annually</span>{" "}
          <span style={{ color: "#96C11F" }}>· just on editing.</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 3.2 }}
          className="mt-12 inline-flex items-center gap-4 rounded-full px-6 py-3 glass-dark"
        >
          <span className="text-xs tracking-[0.32em] uppercase text-white/55">
            Current
          </span>
          <span
            className="text-xl tnum line-through"
            style={{ color: "rgba(200,58,79,0.85)", fontWeight: 700 }}
          >
            £250,000
          </span>
          <span className="text-white/30">→</span>
          <span className="text-xs tracking-[0.32em] uppercase text-white/55">
            New
          </span>
          <span
            className="text-xl tnum"
            style={{ color: "#96C11F", fontWeight: 700 }}
          >
            £7,500
          </span>
        </motion.div>
      </div>
    </SceneShell>
  );
}
